import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Client, clientApi } from '../services/clientApi';
import { Dietitian, dietitianApi } from '../services/dietitianApi';

interface AuthContextType {
  // Dietitian data
  dietitian: Dietitian | null;
  dietitianId: string | null;
  
  // Client data
  client: Client | null;
  clientId: string | null;
  
  // User info
  userEmail: string | null;
  userRole: 'dietitian' | 'client' | null;
  
  // Loading state
  loading: boolean;
  
  // Actions
  setDietitian: (dietitian: Dietitian | null) => void;
  setClient: (client: Client | null) => void;
  loadDietitian: (email: string) => Promise<void>;
  loadClient: (email: string, options?: { forceRefresh?: boolean }) => Promise<void>;
  clearAuth: () => void;
  /** Diyetisyen istek kabul sonrası danışan listesini yenilemek için artırılır */
  clientsRefreshTrigger: number;
  setClientsRefreshTrigger: (v: number | ((prev: number) => number)) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dietitian, setDietitian] = useState<Dietitian | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [dietitianId, setDietitianId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'dietitian' | 'client' | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientsRefreshTrigger, setClientsRefreshTrigger] = useState(0);

  // localStorage'dan verileri yükle
  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const storedEmail = localStorage.getItem('userEmail');
        const storedRole = localStorage.getItem('userRole');
        
        if (storedEmail && storedRole) {
          setUserEmail(storedEmail);
          setUserRole(storedRole as 'dietitian' | 'client');
          
          // Dietitian verisi varsa yükle
          if (storedRole === 'dietitian') {
            const storedDietitian = localStorage.getItem('dietitianData');
            if (storedDietitian) {
              try {
                const dietitianData = JSON.parse(storedDietitian);
                setDietitian(dietitianData);
                setDietitianId(dietitianData.id || null);
              } catch (error) {
                console.error('Dietitian verisi parse edilemedi:', error);
              }
            }
          }
          
          // Client verisi varsa yükle
          if (storedRole === 'client') {
            const storedClient = localStorage.getItem('clientData');
            if (storedClient) {
              try {
                const clientData = JSON.parse(storedClient);
                setClient(clientData);
                setClientId(clientData.id || null);
              } catch (error) {
                console.error('Client verisi parse edilemedi:', error);
              }
            }
          }
        }
      } catch (error) {
        console.error('localStorage yükleme hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFromStorage();
  }, []);

  // Dietitian yükle
  const loadDietitian = useCallback(async (email: string) => {
    try {
      // Önce localStorage'dan kontrol et
      const storedDietitian = localStorage.getItem('dietitianData');
      if (storedDietitian) {
        try {
          const dietitianData = JSON.parse(storedDietitian);
          if (dietitianData && dietitianData.id) {
            setDietitian(dietitianData);
            setDietitianId(dietitianData.id);
            setUserEmail(email);
            setUserRole('dietitian');
            console.log('✅ Dietitian verisi localStorage\'dan yüklendi');
            return;
          }
        } catch (error) {
          console.error('localStorage verisi parse edilemedi:', error);
        }
      }

      // localStorage'da yoksa API'den çek
      console.log('📡 Dietitian verisi API\'den çekiliyor...');
      const dietitianData = await dietitianApi.getDietitianByEmail(email);
      if (dietitianData && dietitianData.id) {
        setDietitian(dietitianData);
        setDietitianId(dietitianData.id);
        setUserEmail(email);
        setUserRole('dietitian');
        localStorage.setItem('dietitianData', JSON.stringify(dietitianData));
        console.log('✅ Dietitian verisi API\'den alındı ve kaydedildi');
      }
    } catch (error) {
      console.error('Dietitian yükleme hatası:', error);
      throw error;
    }
  }, []);

  // Client yükle (forceRefresh: true ise her zaman API'den çek - istek kabul sonrası güncel diyetisyen için)
  const loadClient = useCallback(async (email: string, options?: { forceRefresh?: boolean }) => {
    try {
      if (!options?.forceRefresh) {
        const storedClient = localStorage.getItem('clientData');
        if (storedClient) {
          try {
            const clientData = JSON.parse(storedClient);
            if (clientData && clientData.id) {
              setClient(clientData);
              setClientId(clientData.id);
              setUserEmail(email);
              setUserRole('client');
              return;
            }
          } catch (error) {
            console.error('localStorage verisi parse edilemedi:', error);
          }
        }
      }

      const clientData = await clientApi.getClientByEmail(email);
      if (clientData && clientData.id) {
        setClient(clientData);
        setClientId(clientData.id);
        setUserEmail(email);
        setUserRole('client');
        localStorage.setItem('clientData', JSON.stringify(clientData));
      }
    } catch (error) {
      console.error('Client yükleme hatası:', error);
      throw error;
    }
  }, []);

  // Tüm kullanıcıya özel localStorage verilerini temizle
  const clearAllUserData = useCallback(() => {
    // Temel kullanıcı bilgileri
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('dietitianData');
    localStorage.removeItem('clientData');
    localStorage.removeItem('client-onboarding-completed');
    
    // TaroBot mesajlarını temizle (tüm olası ID'ler için)
    // localStorage'daki tüm taro-messages-* key'lerini bul ve temizle
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('taro-messages-')) {
        localStorage.removeItem(key);
      }
      // Bildirimleri temizle
      if (key.startsWith('notifications_') || key.startsWith('processedMessages_')) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  // Auth temizle
  const clearAuth = useCallback(() => {
    setDietitian(null);
    setClient(null);
    setDietitianId(null);
    setClientId(null);
    setUserEmail(null);
    setUserRole(null);
    clearAllUserData();
  }, [clearAllUserData]);

  // Dietitian setter - localStorage'a da kaydet
  const handleSetDietitian = useCallback((dietitianData: Dietitian | null) => {
    setDietitian(dietitianData);
    setDietitianId(dietitianData?.id || null);
    if (dietitianData) {
      localStorage.setItem('dietitianData', JSON.stringify(dietitianData));
    } else {
      localStorage.removeItem('dietitianData');
    }
  }, []);

  // Client setter - localStorage'a da kaydet
  const handleSetClient = useCallback((clientData: Client | null) => {
    setClient(clientData);
    setClientId(clientData?.id || null);
    if (clientData) {
      localStorage.setItem('clientData', JSON.stringify(clientData));
    } else {
      localStorage.removeItem('clientData');
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        dietitian,
        dietitianId,
        client,
        clientId,
        userEmail,
        userRole,
        loading,
        setDietitian: handleSetDietitian,
        setClient: handleSetClient,
        loadDietitian,
        loadClient,
        clearAuth,
        clientsRefreshTrigger,
        setClientsRefreshTrigger,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

